import { Charge as GqlCharge, SharedAppPaymentDueFragment as GqlPaymentDue } from '@willow/graphql-iso/src/app';
import { Charge, PaymentDue } from '@willow/types-iso';

import { getCurrentEndOfLoanLifeBalances } from '../non-performing/getCurrentEndOfLoanLifeBalances';
import { add, subtract } from '../utils/math';

/**
 * Per-bucket amounts still eligible to be written off once a loan reaches the end of its
 * life (liquidated / paid off). These mirror the post-liquidation residuals shown in the
 * liquidation summary: the balance captured at liquidation time, less any proceeds applied to
 * that bucket, less anything already written off.
 *
 * When an active liquidation exists we read the residual from the stored liquidation record
 * rather than recomputing it live — interest is derived from payment dues, which shift once the
 * loan is liquidated, so a live recompute no longer reflects the balance the liquidation settled.
 *
 * Only interest / corporate advances / fees are derived here — principal, escrow, and
 * suspense write-offs keep their existing snapshot-balance basis.
 */
export interface WriteoffEligibleBalances {
  interest: number;
  corporateAdvances: number;
  fees: number;
}

type LiquidationBalanceLike = { balanceAtTimeOfLiquidation: number; proceedAmountContributed: number };
type LiquidationRecordLike = {
  status: string; // 'active' | 'reversed'
  balances: {
    outstandingInterest: LiquidationBalanceLike;
    corporateAdvances: LiquidationBalanceLike;
    fees: LiquidationBalanceLike;
  };
};
type WriteoffLike = {
  amounts: {
    interest?: number | null;
    corporateAdvances?: number | null;
    fees?: number | null;
  };
};

export function getWriteoffEligibleBalances({
  outstandingBalance,
  escrowBalance,
  deferredAmounts,
  charges,
  paymentDues,
  liquidationRecords,
  writeoffs,
}: {
  outstandingBalance: number;
  escrowBalance: number;
  deferredAmounts: { principal: number; interest: number };
  charges: (Charge | GqlCharge)[];
  paymentDues: PaymentDue[] | GqlPaymentDue[];
  liquidationRecords: LiquidationRecordLike[];
  writeoffs: WriteoffLike[];
}): WriteoffEligibleBalances {
  // iso records carry status 'active'; the GraphQL enum serializes it as 'ACTIVE'. Match both.
  const activeLiquidation = liquidationRecords.find((l) => l.status.toLowerCase() === 'active');

  // Live balances are only needed when the loan was paid off rather than liquidated; for a
  // liquidation we use the residual captured on the record (see note above).
  const endOfLoanLife = activeLiquidation
    ? undefined
    : getCurrentEndOfLoanLifeBalances({ outstandingBalance, escrowBalance, deferredAmounts, charges, paymentDues });

  const writtenOffInterest = add(...writeoffs.map((w) => w.amounts.interest ?? 0));
  const writtenOffCorporateAdvances = add(...writeoffs.map((w) => w.amounts.corporateAdvances ?? 0));
  const writtenOffFees = add(...writeoffs.map((w) => w.amounts.fees ?? 0));

  const residual = (
    liquidationBalance: LiquidationBalanceLike | undefined,
    liveBalance: number,
    writtenOff: number,
  ) => {
    const startingBalance = liquidationBalance
      ? subtract(liquidationBalance.balanceAtTimeOfLiquidation, liquidationBalance.proceedAmountContributed)
      : liveBalance;
    return Math.max(0, subtract(startingBalance, writtenOff));
  };

  return {
    interest: residual(
      activeLiquidation?.balances.outstandingInterest,
      endOfLoanLife?.outstandingInterest ?? 0,
      writtenOffInterest,
    ),
    corporateAdvances: residual(
      activeLiquidation?.balances.corporateAdvances,
      endOfLoanLife?.corporateAdvances ?? 0,
      writtenOffCorporateAdvances,
    ),
    fees: residual(activeLiquidation?.balances.fees, endOfLoanLife?.fees ?? 0, writtenOffFees),
  };
}
